/**
 * Aplica máscara de CPF no formato XXX.XXX.XXX-XX
 * @param value - String contendo números do CPF
 * @returns String formatada do CPF ou string vazia se não houver valor
 * @example
 * maskCPF('12345678901') // retorna '123.456.789-01'
 */
export const maskCPF = (value: string) => {
  if (!value) return '';

  const cleanValue = value.replace(/\D/g, '').slice(0, 11);

  let maskedValue = cleanValue;

  if (cleanValue.length > 9) {
    maskedValue = cleanValue.replace(
      /(\d{3})(\d{3})(\d{3})(\d{2})/,
      '$1.$2.$3-$4'
    );
  } else if (cleanValue.length > 6) {
    maskedValue = cleanValue.replace(/(\d{3})(\d{3})(\d{0,3})/, '$1.$2.$3');
  } else if (cleanValue.length > 3) {
    maskedValue = cleanValue.replace(/(\d{3})(\d{0,3})/, '$1.$2');
  }

  return maskedValue;
};

/**
 * Aplica máscara de CNPJ no formato XX.XXX.XXX/XXXX-XX
 * @param value - String contendo números do CNPJ
 * @returns String formatada do CNPJ ou string vazia se não houver valor
 * @example
 * maskCNPJ('12345678901234') // retorna '12.345.678/9012-34'
 */
export const maskCNPJ = (value: string) => {
  if (!value) return '';

  const cleanValue = value.replace(/\D/g, '').slice(0, 14);

  let maskedValue = cleanValue;

  if (cleanValue.length > 12) {
    maskedValue = cleanValue.replace(
      /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
      '$1.$2.$3/$4-$5'
    );
  } else if (cleanValue.length > 8) {
    maskedValue = cleanValue.replace(
      /(\d{2})(\d{3})(\d{3})(\d{0,4})/,
      '$1.$2.$3/$4'
    );
  } else if (cleanValue.length > 5) {
    maskedValue = cleanValue.replace(/(\d{2})(\d{3})(\d{0,3})/, '$1.$2.$3');
  } else if (cleanValue.length > 2) {
    maskedValue = cleanValue.replace(/(\d{2})(\d{0,3})/, '$1.$2');
  }

  return maskedValue;
};

/**
 * Aplica máscara de telefone no formato (XX) XXXXX-XXXX
 * @param value - String contendo números do telefone
 * @returns String formatada do telefone ou string vazia se não houver valor
 * @example
 * maskPhone('11999998888') // retorna '(11) 99999-8888'
 */
export const maskPhone = (value: string) => {
  if (!value) return '';

  const cleanValue = value.replace(/\D/g, '').slice(0, 11);

  let maskedValue = cleanValue;

  if (cleanValue.length > 10) {
    maskedValue = cleanValue.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  } else if (cleanValue.length > 6) {
    maskedValue = cleanValue.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
  } else if (cleanValue.length > 2) {
    maskedValue = cleanValue.replace(/(\d{2})(\d{0,4})/, '($1) $2');
  }

  return maskedValue;
};

/**
 * Aplica máscara de telefone com código internacional no formato +XX (XX) XXXXX-XXXX
 * @param value - String contendo números do telefone com código internacional
 * @returns String formatada do telefone ou string vazia se não houver valor
 * @example
 * maskPhoneLong('5511999998888') // retorna '+55 (11) 99999-8888'
 */
export const maskPhoneLong = (value: string) => {
  if (!value) return '';

  const cleanValue = value.replace(/\D/g, '').slice(0, 13);

  let maskedValue = cleanValue;

  if (cleanValue.length > 12) {
    maskedValue = cleanValue.replace(
      /(\d{2})(\d{2})(\d{5})(\d{4})/,
      '+$1 ($2) $3-$4'
    );
  } else if (cleanValue.length > 6) {
    maskedValue = cleanValue.replace(/(\d{2})(\d{2})(\d{0,9})/, '+$1 ($2) $3');
  } else if (cleanValue.length > 2) {
    maskedValue = cleanValue.replace(/(\d{2})(\d{0,2})/, '+$1 ($2');
  } else if (cleanValue.length > 0) {
    maskedValue = `+${cleanValue}`;
  }

  return maskedValue;
};

/**
 * Aplica máscara de RG no formato XX.XXX.XXX-X
 * @param value - String contendo números do RG
 * @returns String formatada do RG ou string vazia se não houver valor
 * @example
 * maskRG('123456789') // retorna '12.345.678-9'
 */
export const maskRG = (value: string) => {
  if (!value) return '';

  const cleanValue = value.replace(/\D/g, '').slice(0, 9);

  let maskedValue = cleanValue;

  if (cleanValue.length > 8) {
    maskedValue = cleanValue.replace(
      /(\d{2})(\d{3})(\d{3})(\d)/,
      '$1.$2.$3-$4'
    );
  } else if (cleanValue.length > 5) {
    maskedValue = cleanValue.replace(/(\d{2})(\d{3})(\d{0,3})/, '$1.$2.$3');
  } else if (cleanValue.length > 2) {
    maskedValue = cleanValue.replace(/(\d{2})(\d{0,3})/, '$1.$2');
  }

  return maskedValue;
};

/**
 * Aplica máscara de CEP no formato XXXXX-XXX
 * @param value - String contendo números do CEP
 * @returns String formatada do CEP ou string vazia se não houver valor
 * @example
 * maskCEP('12345678') // retorna '12345-678'
 */
export const maskCEP = (value: string) => {
  if (!value) return '';

  const cleanValue = value.replace(/\D/g, '').slice(0, 8);

  let maskedValue = cleanValue;

  if (cleanValue.length > 5) {
    maskedValue = cleanValue.replace(/(\d{5})(\d{0,3})/, '$1-$2');
  }

  return maskedValue;
};

/**
 * Aplica máscara de IDs de medição do GA4 no formato X-XXXXXXXXXX
 * Aceita combinações alfanuméricas após o hífen, conforme especificação do Google.
 * @param value - String contendo letra inicial e sequência alfanumérica
 * @returns String formatada ou string vazia se não houver valor
 * @example
 * maskGTM('G1234ABCDE1') // retorna 'G-1234ABCDE1'
 */
export const maskGTM = (value: string) => {
  if (!value) return '';

  const normalizedValue = value.toUpperCase();

  // Remove caracteres inválidos e garante que o primeiro seja uma letra
  const alphanumericValue = normalizedValue.replace(/[^A-Z0-9]/g, '');

  const sanitizedValue = alphanumericValue.replace(/^[^A-Z]+/, '').slice(0, 11);

  if (!sanitizedValue) {
    return '';
  }

  if (sanitizedValue.length > 1) {
    return sanitizedValue.replace(/^([A-Z])([A-Z0-9]{0,10})/, '$1-$2');
  }

  return sanitizedValue;
};

/**
 * Formata valor numérico para moeda brasileira (BRL)
 * @param value - String, número ou null/undefined contendo o valor monetário
 * @returns String formatada em reais (R$) ou string vazia se não houver valor
 * @example
 * maskCurrency('1234567') // retorna 'R$ 12.345,67' (string tratada como centavos)
 * maskCurrency(1234.56) // retorna 'R$ 1.234,56' (número tratado como reais)
 * maskCurrency(350) // retorna 'R$ 350,00'
 */
export const maskCurrency = (value: string | number | null | undefined) => {
  // Trata null, undefined e string vazia
  if (value === null || value === undefined || value === '') return '';

  let numberValue: number;

  // Se for número, trata como reais (não divide por 100)
  if (typeof value === 'number') {
    numberValue = value;
  } else {
    // Se for string, trata como centavos (divide por 100)
    const cleanValue = String(value).replace(/\D/g, '');

    // Se não houver dígitos após limpar, retorna vazio
    if (!cleanValue) return '';

    numberValue = parseInt(cleanValue, 10) / 100;
  }

  // Formata o número com 2 casas decimais
  const formattedValue = numberValue.toFixed(2);
  const numberParts = formattedValue.split('.');
  let integerPart = numberParts[0];
  const decimalPart = numberParts[1];

  if (integerPart) {
    // eslint-disable-next-line regexp/no-unused-capturing-group, sonarjs/slow-regex
    integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  return `R$ ${integerPart},${decimalPart}`;
};

/**
 * Aplica máscara de data no formato DD/MM/YYYY
 * @param value - String contendo números da data
 * @returns String formatada da data ou string vazia se não houver valor
 * @example
 * maskDate('12122023') // retorna '12/12/2023'
 */
export const maskDate = (value: string) => {
  if (!value) return '';

  const cleanValue = value.replace(/\D/g, '').slice(0, 8);

  let maskedValue = cleanValue;

  if (cleanValue.length > 4) {
    maskedValue = cleanValue.replace(/(\d{2})(\d{2})(\d{0,4})/, '$1/$2/$3');
  } else if (cleanValue.length > 2) {
    maskedValue = cleanValue.replace(/(\d{2})(\d{0,2})/, '$1/$2');
  }

  return maskedValue;
};

/**
 * Formata número de telefone baseado no tamanho da string de entrada
 * @param value - String contendo números do telefone
 * @returns String formatada do telefone de acordo com o tamanho ou valor original se não corresponder a nenhum formato
 * @example
 * formatPhoneNumber('11999998888') // retorna '(11) 99999-8888'
 * formatPhoneNumber('999998888') // retorna '99999-8888'
 */
export const formatPhoneNumber = (value: string) => {
  if (!value) return value;

  const cleanValue = value.replace(/\D/g, '');

  switch (cleanValue.length) {
    case 8:
      return cleanValue.replace(/(\d{4})(\d{4})/, '$1-$2');
    case 9:
      return cleanValue.replace(/(\d{5})(\d{4})/, '$1-$2');
    case 10:
      return cleanValue.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    case 11:
      return cleanValue.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    case 13:
      return cleanValue.replace(
        /(\d{2})(\d{2})(\d{5})(\d{4})/,
        '+$1 ($2) $3-$4'
      );
    default:
      return value;
  }
};

/**
 * Aplica máscara de hora no formato HH:MM
 * @param value - String contendo números da hora
 * @returns String formatada da hora ou string vazia se não houver valor
 * @example
 * maskHora('1234') // retorna '12:34'
 */
export const maskHora = (value: string) => {
  if (!value) return '';

  const cleanValue = value.replace(/\D/g, '').slice(0, 5);

  let maskedValue = cleanValue;

  if (cleanValue.length > 1) {
    maskedValue = cleanValue.replace(/(\d{2})(\d)/, '$1:$2');
  }

  return maskedValue;
};
